import { FieldInputValue, InputFieldProps } from 'components/Forms';
import {
  AdjustmentType,
  DayOfWeek,
  ScheduleAdjustmentReason
} from 'models/Adjustment';
import AdjustmentGroup from 'models/AdjustmentGroup';
import { getAdjustmentChange, getAdjustmentForDay } from 'utils/adjustments';
import {
  TemporaryAdjustmentConstants,
  atLeastOneFilledSchema
} from 'views/Engineers/utils';
import * as Yup from 'yup';
import {
  adjustmentCommonFieldsInitialValues,
  adjustmentCommonFieldsValidationSchema,
  adjustmentWeekdayKeys,
  getAdjustmentCommonFields
} from './adjustmentFormCommonConfig';

export const getDayFieldName = (day: string) => `maxTravelChange${day}`;

export const maxTravelToLabel = (maxTravel: number) => `${maxTravel} mins`;

const tenMinuteIncrement = 10;
const thirtyMinuteIncrement = 30;
const tenMinuteIncrementMax = 180;
const thirtyMinuteIncrementStart = 210;
const thirtyMinuteIncrementMax = 480;

const maxTravelOptionValues = () => {
  const output: number[] = [];

  for (
    let i = tenMinuteIncrement;
    i <= tenMinuteIncrementMax;
    i += tenMinuteIncrement
  ) {
    output.push(i);
  }

  for (
    let i = thirtyMinuteIncrementStart;
    i <= thirtyMinuteIncrementMax;
    i += thirtyMinuteIncrement
  ) {
    output.push(i);
  }

  return output;
};

export const maxTravelOptions = maxTravelOptionValues().map(optionValue => {
  const label = maxTravelToLabel(optionValue);

  return {
    value: optionValue.toString(),
    label,
    key: label
  };
});

const getMaxTravelFieldForWeekday = (day: string): InputFieldProps => {
  const fieldName = getDayFieldName(day);
  return {
    id: fieldName,
    name: fieldName,
    qaId: fieldName,
    disablePlaceholder: true,
    inputType: 'select',
    options: maxTravelOptions
  };
};

const maxTravelReasonOptions = (): FieldInputValue[] => {
  return [
    {
      value: ScheduleAdjustmentReason.Mobilisation,
      label: ScheduleAdjustmentReason.Mobilisation,
      key: 'mobilisation'
    },
    {
      value: ScheduleAdjustmentReason.TimeBasedAvailability,
      label: ScheduleAdjustmentReason.TimeBasedAvailability,
      key: 'timeBasedAvailability'
    },
    {
      value: ScheduleAdjustmentReason.MaxTravelAdjustment,
      label: ScheduleAdjustmentReason.MaxTravelAdjustment,
      key: 'maxTravelAdjustment'
    }
  ];
};

export const maxTravelFormCommonFields = getAdjustmentCommonFields(
  maxTravelReasonOptions()
);

export const maxTravelFormWeekdayFields = Object.fromEntries(
  adjustmentWeekdayKeys.map(day => [day, getMaxTravelFieldForWeekday(day)])
);

export const maxTravelFormValidationSchema = Yup.object()
  .shape(adjustmentCommonFieldsValidationSchema)
  .test('atLeastOneFilled', function validOneShiftPattern(formValues) {
    return atLeastOneFilledSchema(
      formValues,
      getDayFieldName,
      this.createError
    );
  });

const getWeekdayMaxTravelFromGroup = (
  day: DayOfWeek,
  adjustmentGroup: AdjustmentGroup
) => {
  const adjustment = getAdjustmentForDay(day, adjustmentGroup.adjustments);
  return adjustment
    ? getAdjustmentChange(adjustment, AdjustmentType.MaxTravel)?.change
    : undefined;
};

export const maxTravelFormWeekdaysInitialValues = (
  adjustmentGroup: AdjustmentGroup | undefined
) => {
  const fieldNameMaxTravelPairs = adjustmentWeekdayKeys.map(day => {
    const adjustmentGroupMaxTravel = adjustmentGroup
      ? getWeekdayMaxTravelFromGroup(
          day.toUpperCase() as DayOfWeek,
          adjustmentGroup
        )
      : null;

    return [
      getDayFieldName(day),
      adjustmentGroupMaxTravel || TemporaryAdjustmentConstants.Empty
    ];
  });

  return {
    ...Object.fromEntries(fieldNameMaxTravelPairs)
  };
};

export const maxTravelFormInitialValues = (
  adjustmentGroup?: AdjustmentGroup
) => {
  const maxTravelDaysInitialValues = maxTravelFormWeekdaysInitialValues(
    adjustmentGroup
  );

  return {
    ...adjustmentCommonFieldsInitialValues(adjustmentGroup?.adjustments[0]),
    ...maxTravelDaysInitialValues
  };
};
